import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { combineLatest, Observable, Subject, takeUntil } from 'rxjs';
import { Insight } from 'src/app/models/insight';
import { ACTIONS, STATUSES } from 'src/app/shared/constants/general-constants';
import { SharedModule } from '../../../shared/shared.module';
import { InsightsState, LoadListOfFailedJobs, ReloadInsights } from '../redux';
import { LoadInsights, LoadInsightsInactive, ReloadInsightsInactive } from '../redux/insights.actions';
import { DeleteInsightComponent } from './delete-insight/delete-insight.component';
import { EditInsightComponent } from './edit-insight/edit-insight.component';
import { FailedJobInsightListComponent } from './failed-job-insight-list/failed-job-insight-list.component';
import { StatsInsightComponent } from './stats-insight/stats-insight.component';
import { ViewInsightComponent } from './view-insight/view-insight.component';

@Component({
  selector: 'app-insights',
  templateUrl: './insights.component.html',
  styleUrls: ['./insights.component.css'],
  imports: [SharedModule, RouterOutlet]
})
export class InsightsComponent implements OnInit, OnDestroy {
  public activeInsight$: Observable<any[]> = inject(Store).select(InsightsState.insightsActive);
  public inactiveInsight$: Observable<any[]> = inject(Store).select(InsightsState.insightsInactive);
  public loading$: Observable<boolean> = inject(Store).select(InsightsState.insightSpinner);
  public failedJob$: Observable<any[]> = inject(Store).select(InsightsState.failedJobs);
  private unsubscribe = new Subject<void>();

  public tableData?: Insight[];
  public showActions = [ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.STATS, ACTIONS.CLONE, ACTIONS.DELETE];
  public failedInsights?: any[];

  constructor(
    private store: Store,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private router: Router
  ) {
    if (!this.store.selectSnapshot(InsightsState.failedJobs)) {
      this.store.dispatch(LoadListOfFailedJobs);
    }
    if (!this.store.selectSnapshot(InsightsState.insightsActive)) {
      this.store.dispatch(LoadInsights);
    }
    if (!this.store.selectSnapshot(InsightsState.insightsInactive)) {
      this.store.dispatch(LoadInsightsInactive);
    }
    combineLatest([this.activeInsight$, this.inactiveInsight$])
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(([active, inactive]) => (this.tableData = [...(active ?? []), ...(inactive ?? [])]));
  }

  public ngOnInit() {
    this.failedJob$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data) {
        this.failedInsights = data
          .filter((job) => job?.type === 'insight')
          .slice()
          .sort(this.sortingByDate);
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clickedButton($event: { action: any; data?: any }) {
    if ($event.action === ACTIONS.REFRESH) {
      this.tableData = undefined;
      this.store.dispatch(ReloadInsightsInactive);
      this.store.dispatch(ReloadInsights);
    }
    if ($event.action === ACTIONS.FAILED_INSIGHTS) {
      this.dialogService.open(FailedJobInsightListComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          failedJobsList: this.failedInsights
        }
      });
    }
    if ($event.action === ACTIONS.CREATE) {
      this.dialogService.open(EditInsightComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          insight: new Insight()
        }
      });
    }
    if ($event.action === ACTIONS.CLONE) {
      const clonedInsight = Insight.clone($event.data);
      clonedInsight.id = undefined;
      this.dialogService.open(EditInsightComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          insight: clonedInsight
        }
      });
    }
    if ($event.action === ACTIONS.VIEW) {
      const insight: Insight = $event.data;
      this.router.navigate([`/insights/table/` + insight.id]);
    }
    if ($event.action === ACTIONS.STATS) {
      const insight: Insight = $event.data;
      this.dialogService.open(StatsInsightComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          insight
        }
      });
    }
    if ($event.action === ACTIONS.EDIT) {
      const insight: Insight = $event.data;
      if (insight.status === STATUSES.RUNNING) {
        this.toastrService.show(status, $localize`Cannot edit a running insight`, { status: 'danger' });
      } else {
        this.dialogService.open(EditInsightComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            action: $event.action,
            insight: Insight.copy(insight)
          }
        });
      }
    }
    if ($event.action === ACTIONS.DELETE) {
      const insight: Insight = $event.data;
      this.dialogService.open(DeleteInsightComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          insight: Insight.copy(insight) as Insight
        }
      });
    }
  }

  public sortingByDate(a: any, b: any) {
    if (a.date_created > b.date_created) {
      return -1;
    }
    if (a.date_created < b.date_created) {
      return 1;
    }

    return 0;
  }
}
